/// <amd-module name="Widgets/Burndown/BurndownWidgetComponents/BurndownViewComponent" />
import * as AnalyticsResources from "Analytics/Common/Resources";
import { AnalyticsExceptionType, extractODataError, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import * as React from "react";
import { showActionRequiredDialog } from "Reporting/Lightbox/Lightbox";
import { ChartAndSubmetricComponentLayouts, ComponentLayoutMapping } from "Reporting/Visuals/LayoutComponents/Widgets/ComponentLayoutEngine";
import { LayoutState, MessageType, packMessageAsState } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutState";
import { getProject } from "Tfs/Widget/Context";
import { startsWith } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { ActionCreatorBase } from "WidgetCommon/ModernWidgetTypes/ActionCreatorBase";
import { AggregationMode } from "WidgetCommon/ModernWidgetTypes/CommonConfigurationTypes";
import { SettingsManagerBase } from "WidgetCommon/ModernWidgetTypes/SettingsManagerBase";
import { ViewComponentBase, ViewComponentProps } from "WidgetCommon/ModernWidgetTypes/ViewComponentBase";
import { WidgetDataManagerBase, WidgetDataManagerOptions } from "WidgetCommon/ModernWidgetTypes/WidgetDataManagerBase";
import { ActionMessageOptions, MessageOptions } from "WidgetCommon/ModernWidgetTypes/WidgetMessageCardFactory";
import * as Resources from "Widgets/Burndown/Resources";
import { BurnDirection } from "./BurnDirection";
import { BurndownActionCreator } from "./BurndownActionCreator";
import { BurndownDataManager, BurnupDataManager } from "./BurndownDataManager";
import { BurndownSettings, DateSampleMode } from "./BurndownSettings";
import BurndownSettingsManager from "./BurndownSettingsManager";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { BurndownConstants } from "./BurndownConstants";

export interface BurndownViewComponentProps extends ViewComponentProps {
    burnDirection: BurnDirection;
}

export default class BurndownViewComponent extends ViewComponentBase<BurndownViewComponentProps, BurndownSettings | undefined> {
    protected createDataManager(options: WidgetDataManagerOptions): WidgetDataManagerBase {
        if (this.props.burnDirection === BurnDirection.Down) {
            return new BurndownDataManager(this.context.pageContext, this.props.teamContext, options, !this.props.openLinksInNewTab);
        } else {
            return new BurnupDataManager(this.context.pageContext, this.props.teamContext, options, !this.props.openLinksInNewTab);
        }
    }

    /** Describes the set of layout mappings supported by this widget. */
    protected getLayoutMappings(): ComponentLayoutMapping[] {
        return ChartAndSubmetricComponentLayouts.getDefaultMappings();
    }

    protected createActionCreator(): ActionCreatorBase {
        return new BurndownActionCreator(
            this.context.pageContext,
            this.dataManager,
            this.store,
            this.actions,
            this.props.contributionId,
            extractODataError
        );
    }

    protected createSettingsManager(): SettingsManagerBase<BurndownSettings> {
        // Because this widget doesn't have a default state, generate empty settings
        return new BurndownSettingsManager(this.context.pageContext, this.props.teamContext);
    }

    protected getMessageOptions(): MessageOptions | undefined {
        return getMessageOptions(this.context.pageContext, this.getStoreState(), this.props);
    }

    protected handleSettingsError(error: Error) {
        if (recognizeAnalyticsException(error) === AnalyticsExceptionType.MissingAnalyticsPermissions) {
            this.store.setState(packMessageAsState(MessageType.MissingPermissions));
            this.setStoreStateAndRenderCompletion();
            return;
        }

        super.handleSettingsError(error);
    }

    /** Pass custom telemetry about the load event for Standardized Widget Reporting. */
    protected packWidgetLoadedTelemetryData(burndownSettings: BurndownSettings): { [key: string]: any } {
        return packWidgetLoadedTelemetryData(this.context.pageContext, burndownSettings);
    }
}

export function getMessageOptions(
    pageContext: IVssPageContext,
    layoutState: LayoutState,
    viewComponentProps: ViewComponentProps
): MessageOptions | undefined {
    const messageType = layoutState.messageType;
    let options: MessageOptions | undefined;

    switch (messageType) {
        case MessageType.AxFaultIn:
            options = {
                actionText: Resources.BurndownWidget_LearnMore,
                actionAriaLabel: Resources.BurndownWidget_LearnMore,
                actionOnClickDelegate: () => {
                    window.open(Resources.BurndownWidget_LearnMoreLink, "_blank");
                }
            } as ActionMessageOptions;
            break;

        case MessageType.MissingPermissions:
            options = {
                actionOnClickDelegate: () => {
                    showActionRequiredDialog(
                        pageContext,
                        AnalyticsResources.PermissionDeniedTitleText,
                        <FormatComponent format={AnalyticsResources.PermissionDeniedDetailText}>
                            <a href={AnalyticsResources.PermissionDeniedDetailLinkAddress}>{AnalyticsResources.PermissionDeniedDetailLinkText}</a>
                        </FormatComponent>
                    );
                }
            } as ActionMessageOptions;
            break;
        case MessageType.Unconfigured:
            options = {
                actionOnClickDelegate: viewComponentProps.onConfigure
            } as ActionMessageOptions;
            break;
        case MessageType.SetIterationDates:
            const linkToSetIterations = routeUrl(pageContext, "ms.vss-admin-web.project-admin-hub-route", {
                project: getProject(pageContext).name,
                adminPivot: "work"
            });
            options = {
                actionText: Resources.BurndownWidget_SetIterationDatesLink,
                actionAriaLabel: Resources.BurndownWidget_SetIterationDatesAriaLabel,
                actionOnClickDelegate: () => window.open(linkToSetIterations, "_blank")
            } as ActionMessageOptions;
            break;
        default:
            options = undefined;
            break;
    }

    return options;
}

export function packWidgetLoadedTelemetryData(pageContext: IVssPageContext, burndownSettings: BurndownSettings): { [key: string]: any } {
    if (burndownSettings) {
        return {
            totalScopeTrendlineEnabled: burndownSettings.totalScopeTrendlineEnabled,
            completedWorkEnabled: burndownSettings.completedWorkEnabled,
            stackByWorkItemTypeEnabled: burndownSettings.stackByWorkItemTypeEnabled,
            WorkItemFilterMode: burndownSettings.workItemTypeFilter.identifier, // Question: Is the widget using backlogs or work items?
            includeBugsEnabled: burndownSettings.includeBugsForRequirementCategory,
            aggregationMode: burndownSettings.aggregation && AggregationMode[burndownSettings.aggregation.identifier], // Question: Is the user using Sum or Count
            aggregationField: burndownSettings.aggregation && getAggregationFieldName(pageContext, burndownSettings.aggregation.settings), // Question: What field is being summed on here?
            showResolvedItemsAsCompletedEnabled: burndownSettings.showResolvedItemsAsCompletedEnabled,
            plotTimeByDates:
                burndownSettings.timePeriodConfiguration &&
                burndownSettings.timePeriodConfiguration.samplingConfiguration &&
                DateSampleMode[burndownSettings.timePeriodConfiguration.samplingConfiguration.identifier] // Question: Are users using iterations or dates?
        };
    } else {
        return {};
    }
}

// Pass the Field name being aggregated on in lower case 
function getAggregationFieldName(pageContext: IVssPageContext, fieldName: string | undefined): string | undefined {
    if (fieldName == null) {
        return fieldName;
    }

    const lowerCasedName = fieldName.toLowerCase();

    if(isFeatureEnabled(pageContext, BurndownConstants.featureFlagName, false)){
        // If we are displaying Custom Work Items in the Burndown widget, return all fields
        return lowerCasedName;
    }

    // Otherwise restrict to field names with first party prefixes, and require presence of "." character, which is blocked for custom fields
    const allowField = startsWith(lowerCasedName, "microsoft.") || startsWith(lowerCasedName, "system.");
    return allowField ? fieldName : "(CUSTOM)";
}
